// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { SortDirection } from '@/constants/sort';
import { currencyAmountSchema, currencySchema } from '@/currency/api/types';
import { isoCurrencyCodeSchema } from '@/currency/constants/iso';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

// Day counts and carrying costs from the server fail the response when
// unparseable, so a malformed value reaches the support snackbar. On the read
// path the server is the source of truth for bounds, so dollarStringSchema's
// outbound cap does not apply.

/** Parses a server decimal day count, failing the response when it is not a number. */
export const dayCountSchema = z.string().transform((value, ctx) => {
  const dayCount = Number(value);
  if (value.trim() === '' || Number.isNaN(dayCount)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Unparseable day count: "${value}"`,
    });
    return z.NEVER;
  }

  return dayCount;
});

// Per-segment carrying-cost breakdown for the row. A field is null when
// the segment doesn't apply (segment day value is also null); a segment
// that applies but clamps to zero positive days renders as "$0.00". The
// FE shows these as a breakdown tooltip on the carrying-cost cell. Date
// lag is absent because it's a label gap, not a waiting interval, so it
// has no carrying-cost contribution.
const invoiceLifecycleCarryingCostsSchema = z.object({
  chargeLag: currencyAmountSchema.nullable(),
  invoiceReview: currencyAmountSchema.nullable(),
  deliveryLag: currencyAmountSchema.nullable(),
  actualTerms: currencyAmountSchema.nullable(),
  delinquency: currencyAmountSchema.nullable(),
});

// A single row in the billing lifecycle report's detail table. Segment day
// fields are nullable because an invoice may not have reached the state
// that defines each segment (e.g. delinquency is null until the invoice is
// paid or overdue). carryingCost is the sum of the per-segment
// carryingCosts breakdown (segments that don't apply contribute 0 to the
// sum), and is null when every segment is null (pure draft with no
// lifecycle events). The seven anchor dates power the per-invoice Gantt
// accordion.
const invoiceLifecycleRowSchema = z.object({
  invoiceKey: z.string(),
  invoiceNumber: z.string(),
  customerKey: z.string().nullable(),
  customerName: z.string().nullable(),
  projectKey: z.string().nullable(),
  projectName: z.string().nullable(),
  amount: currencySchema,
  currency: z.preprocess(
    (v) => (typeof v === 'string' ? v.toUpperCase() : v),
    isoCurrencyCodeSchema
  ),
  chargeLag: dayCountSchema.nullable(),
  invoiceReview: dayCountSchema.nullable(),
  deliveryLag: dayCountSchema.nullable(),
  dateLag: z.number().nullable(),
  actualTerms: z.number().nullable(),
  delinquency: z.number().nullable(),
  totalCycle: dayCountSchema.nullable(),
  billingEligibleDate: dateSchema.nullable(),
  draftedAt: dateTimeSchema.nullable(),
  openedAt: dateTimeSchema.nullable(),
  firstSentAt: dateTimeSchema.nullable(),
  invoiceDate: dateSchema.nullable(),
  dueDate: dateSchema.nullable(),
  paidDate: dateSchema.nullable(),
  carryingCosts: invoiceLifecycleCarryingCostsSchema,
  carryingCost: currencyAmountSchema.nullable(),
});

export type InvoiceLifecycleRow = z.infer<typeof invoiceLifecycleRowSchema>;

const invoiceLifecycleListingSchema = z.object({
  rows: z.array(invoiceLifecycleRowSchema),
  totalCount: z.number(),
});

type InvoiceLifecycleListing = z.infer<typeof invoiceLifecycleListingSchema>;

const invoiceLifecycleListingResponseSchema = z.object({
  data: invoiceLifecycleListingSchema,
  metadata: responseMetadataSchema,
});

export interface InvoiceLifecycleListingFilter {
  windowDays?: number;
  customerKeys?: string[];
  projectKeys?: string[];
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: SortDirection;
}

function serializeFilter(filter: InvoiceLifecycleListingFilter): string {
  const params = new URLSearchParams();
  if (filter.windowDays !== undefined) {
    params.set('windowDays', String(filter.windowDays));
  }
  for (const key of filter.customerKeys ?? []) {
    params.append('customerKey', key);
  }
  for (const key of filter.projectKeys ?? []) {
    params.append('projectKey', key);
  }
  if (filter.search !== undefined && filter.search.length > 0) {
    params.set('search', filter.search);
  }
  if (filter.page !== undefined) {
    params.set('page', String(filter.page));
  }
  if (filter.pageSize !== undefined) {
    params.set('limit', String(filter.pageSize));
  }
  if (filter.sortBy !== undefined && filter.sortBy.length > 0) {
    params.set('sortBy', filter.sortBy);
  }
  if (filter.sortOrder !== undefined) {
    params.set('sortOrder', filter.sortOrder);
  }
  return params.toString();
}

export async function getInvoiceLifecycleListing(
  filter: InvoiceLifecycleListingFilter
): Promise<InvoiceLifecycleListing> {
  const qs = serializeFilter(filter);
  const url =
    qs.length > 0 ? `/v1/invoice-lifecycle?${qs}` : '/v1/invoice-lifecycle';
  const response = await api.get(url);
  const result = await parseResponse(
    invoiceLifecycleListingResponseSchema,
    response.data,
    { schemaName: 'InvoiceLifecycleListing', endpoint: url }
  );
  return result.data;
}
